import { hasModules } from "csi/_base/modules";
import def from "csi/def";
import Tab from "csi/def/Tab";
import locale from "csi/locale";
import csi from "csi/main";
import { ReactViewProps } from "csi/middleware/common";
import ReactView from "csi/react/View";
import reflection from "csi/reflection";
import { getAccessRule, getTypeDefWithView } from "csi/service/viewService";
import LayoutStore, { LayoutItem, LayoutItemOptions } from "csi/state/layout";
import { VIEW_TYPE_TABS } from "csi/state/viewUtils";
import TabsView from "csi/tabs/View";
import { C8Node, C8NodeType, cnlPath } from "csi/typings";
import { extendUrlPath } from "csi/utility/pathUtility";
import { flatten } from "lodash";
import { FunctionComponent } from "react";

type CustomTabNameFunction = (node: C8Node) => string;
const _customTabNames: Record<C8NodeType, Record<string, CustomTabNameFunction>> = {};

const DEBUG_TAB = "Debug";

interface ReactComponentInfo {
	/** If the view is a React view	*/
	isReact: boolean;

	/** React component */
	ReactComponent?: FunctionComponent<any>;

	/** View definition if view is a React view */
	reactView: ReactView;

	/** View properties */
	reactViewProps: ReactViewProps;
}

/**
 * Get React component and whether the view is a React view to enable rendering of both Dojo and React views
 */
export const getReactComponentInfo = (node, viewId): ReactComponentInfo => {
	const reactView = def.getNodeViewDef(node, viewId) as ReactView;
	const componentPath = reactView?.componentName;
	const isReact = !!componentPath;
	const ReactComponent = isReact ? csi.syncRequire(componentPath)?.default : null;
	const reactViewProps: ReactViewProps = { node };

	return {
		reactView,
		isReact,
		ReactComponent,
		reactViewProps,
	};
};

export function registerCustomTabName(type: string, id: string, func: (node: C8Node) => string): void {
	if (!_customTabNames[type]) {
		_customTabNames[type] = {};
	}
	_customTabNames[type][id] = func;
}

/**
 * Add debug tab if currently navigated to site node
 */
export async function enableDebugMode(layoutStore: LayoutStore): Promise<void> {
	if (layoutStore.type === reflection.TYPE_SITE) {
		const item = layoutStore.findItem(DEBUG_TAB);
		await resolveLayoutItems(layoutStore, [item]);
	}
}
/**
 * Convert tab definition to layout options
 */
export function getTabLayoutOptions(tab: Tab): LayoutItemOptions {
	// NOTE: eventually this may not be needed, but layout store cannot accept module dependencies of Tab/Rule
	const {
		m_path,
		m_rules,
		m_url,
		accessTab,
		bReadOnly,
		switchboard,
		target,
	} = tab;

	return {
		accessFunction: csi.resolveFunction(accessTab),
		forceReadOnly: bReadOnly,
		path: m_path,
		rules: m_rules?.map(getAccessRule),
		switchboard,
		targetType: target,
		url: m_url,
	};
}

/**
 * Return filtered list of tabs based on required modules
 */
export function getInstalledTabs(tabsView: TabsView): Tab[] {
	return tabsView.tabs?.filter((tab) => {
		const { modules, notModules } = tab;
		return (!modules || hasModules(modules)) && (!notModules || !hasModules(notModules));
	}) || [];
}

/** 
 * Determine type of node owning view 
 */
export function getViewOwnerType(item: LayoutItem): string | null {
	const { cache, node, options, urlPath } = item;
	const [url, path] = urlPath;
	let type = options?.targetType || node?.$Type;

	if (!type && path) {
		// it's possible the root node exists, but some node along the path does not; use model instead
		const rootNode = cache.get(url);
		const rc = reflection.getNodeClass(rootNode);
		const rcTarget = rc?.getPathClass(path, true);
		type = rcTarget?.getType();
	}

	return type;
}

/**
 * Populate layout with content from given view
 */
export async function populateLayoutStore(layoutStore: LayoutStore, tabsView: TabsView): Promise<void> {
	const { addItem, rootUrl } = layoutStore;
	const installedTabs = getInstalledTabs(tabsView);
	const items = installedTabs.map((tab) => addItem(tab.m_idView, getTabLayoutOptions(tab)));
	const dependencyPaths: cnlPath[] = tabsView.m_depPaths?.map((path) => [rootUrl, path]);

	if (layoutStore.type === reflection.TYPE_SITE) {
		items.push(layoutStore.addItem(DEBUG_TAB));
	}

	// query for nodes needed to determine views in layout
	await resolveLayoutItems(layoutStore, items, dependencyPaths);
}

/**
 * Resolve layout items for tabs, and recursively add any sub-tabs
 */
async function resolveLayoutItems(layoutStore: LayoutStore, items: LayoutItem[], dependencyPaths?: cnlPath[]): Promise<void> {
	try {
		await layoutStore.resolveItems(items, dependencyPaths);

		// resolve view for each item to determine if there are nested tabs
		const itemsChildren = [];
		const itemsPaths = [];

		for (const item of items) {
			const { id, node, options } = item;
			const type = getViewOwnerType(item);
			const typeDef = type ? getTypeDefWithView(reflection.getClass(type), id) : null;

			if (!typeDef) {
				continue;
			}

			const [viewType, helpUseRootType] = typeDef.getViewTypeAndHelpRootType(id);

			if (viewType) {
				const lr = locale.getResolver({ typeContext: type });

				const customNameFunc = _customTabNames[type]?.[id];
				const name = customNameFunc?.(node) || lr.getTypeView(type, id);

				item.resolveView(name, typeDef.getType(), helpUseRootType);

				if (viewType === VIEW_TYPE_TABS) {
					const tabsView = typeDef.getViewDef(id, viewType) as TabsView;
					const children = getInstalledTabs(tabsView)
						.map((tab) => item.addChild(tab.m_idView, getTabLayoutOptions(tab)));
					const paths = tabsView.m_depPaths;

					item.headerViewId = tabsView.idHeaderView;
					itemsChildren.push(children);

					if (paths) {
						const { urlPath } = item;
						const itemPaths = paths.map((path) => extendUrlPath(urlPath, path));
						itemsPaths.push(itemPaths);
					}
				} else if (node && options?.switchboard) {
					// resolve switchboard action
					options.switchboard.resolve(lr, reflection.getNodeClass(node), node);
				}
			}
		}

		// if nested tabs, then resolve them too
		const subitems = flatten(itemsChildren);
		const subpaths = flatten(itemsPaths);

		if (subitems.length || subpaths.length) {
			await resolveLayoutItems(layoutStore, subitems, subpaths);
		}
	} catch (err) {
		// TODO - what to do with error?
		console.log(err);
	}
}